import { RouteDetailsStrings, type RouteDetailsStringSet } from './strings'

/**
 * Convenient-transfer car positions. Mirrors iOS
 * `ConvenientTransferCarType` and Android's enum — same names, same
 * highlight-index map, same caption fragments (QA spec §3.3).
 *
 * Multi-type captions sort by **car position** (front→back of train:
 * first → nearFirst → center → nearEnd → end) before joining with
 * " or ", so the caption reads first-then-last to match Figma S4 — NOT
 * by the V1 enum `rawValue`, which is an arbitrary declaration order
 * (cross-platform fix, 2026-05-25). The car position IS the first (and
 * for single-cell types, only) highlight index.
 */
export type ConvenientCarType = 'first' | 'nearFirst' | 'center' | 'nearEnd' | 'end' | 'all'

/** The five car cells (indices 0–4) a type highlights. */
const HIGHLIGHT_INDICES: Record<ConvenientCarType, readonly number[]> = {
  first: [0],
  nearFirst: [1],
  center: [2],
  nearEnd: [3],
  end: [4],
  all: [0, 1, 2, 3, 4],
}

/**
 * SCREAMING_SNAKE wire token → `ConvenientCarType` (cross-platform
 * contract: backend/schema emits FIRST / NEAR_FIRST / CENTER / NEAR_END
 * / END / ALL; iOS + Android decode the same tokens).
 */
const WIRE_TO_CAR: Record<string, ConvenientCarType> = {
  FIRST: 'first',
  NEAR_FIRST: 'nearFirst',
  CENTER: 'center',
  NEAR_END: 'nearEnd',
  END: 'end',
  ALL: 'all',
}

/** Fallback when a wire token has no mapping (cross-platform contract). */
const UNMATCHED_FALLBACK: readonly ConvenientCarType[] = ['nearFirst', 'nearEnd']

/**
 * Decodes the convenient-cars wire field (a list of SCREAMING_SNAKE
 * tokens on a transfer) into `ConvenientCarType`s. Unknown / empty input
 * degrades to the `[nearFirst, nearEnd]` fallback (matches iOS/Android).
 * Duplicates collapse; order follows the input.
 */
export function parseConvenientCars(wire: readonly string[]): readonly ConvenientCarType[] {
  const out: ConvenientCarType[] = []
  for (const token of wire) {
    const car = WIRE_TO_CAR[token]
    if (car !== undefined && !out.includes(car)) out.push(car)
  }
  return out.length > 0 ? out : [...UNMATCHED_FALLBACK]
}

/** Front→back car position, used to order caption fragments. */
function carPosition(car: Exclude<ConvenientCarType, 'all'>): number {
  return HIGHLIGHT_INDICES[car][0]
}

/** Per-type caption fragment (V1 `RouteDetails.Transfer.*`). */
function carFragment(
  car: Exclude<ConvenientCarType, 'all'>,
  strings: RouteDetailsStringSet,
): string {
  switch (car) {
    case 'first':
      return strings.carFirst
    case 'nearFirst':
      return strings.carNearFirst
    case 'center':
      return strings.carMiddle
    case 'nearEnd':
      return strings.carNearEnd
    case 'end':
      return strings.carLast
  }
}

/** Set of highlighted cell indices for the given car types. */
export function highlightedCarIndices(cars: readonly ConvenientCarType[]): ReadonlySet<number> {
  const set = new Set<number>()
  for (const car of cars) {
    for (const i of HIGHLIGHT_INDICES[car]) set.add(i)
  }
  return set
}

/**
 * Assembles the PossibleTransfer caption (V1 `_transferText`):
 *   "<prefix> <fragment>[ or <fragment>]*"
 * `.all` collapses to "<prefix> all cars". Types are sorted by **car
 * position** (front→back) before joining so the caption reads
 * first-then-last, matching Figma S4 and identical across platforms.
 */
export function transferCaption(
  cars: readonly ConvenientCarType[],
  strings: RouteDetailsStringSet = RouteDetailsStrings,
): string {
  const prefix = strings.forTransferPrefix
  if (cars.length === 0) return ''
  if (cars.includes('all')) return `${prefix} ${strings.carAll}`
  const fragments = [...cars]
    .filter((c): c is Exclude<ConvenientCarType, 'all'> => c !== 'all')
    .sort((a, b) => carPosition(a) - carPosition(b))
    .map((c) => carFragment(c, strings))
  return `${prefix} ${fragments.join(strings.carOr)}`
}
